import { useEffect } from 'react'
import { observer } from 'mobx-react-lite'
import { useParams, useNavigate } from 'react-router-dom'
import { formatDistanceToNow } from 'date-fns'
import toast from 'react-hot-toast'
import Card from '@/components/common/Card'
import Button from '@/components/common/Button'
import Badge from '@/components/common/Badge'
import { crewStore } from '@/stores/CrewStore'
import {
  ArrowLeftIcon,
  PlayIcon,
  CheckCircleIcon,
  XCircleIcon,
  UserIcon,
  ArrowsRightLeftIcon,
  Squares2X2Icon,
  ChartBarSquareIcon,
  CpuChipIcon,
  BoltIcon,
  BeakerIcon,
} from '@heroicons/react/24/outline'

const CrewDetailPage = observer(() => {
  const { name } = useParams<{ name: string }>()
  const navigate = useNavigate()

  useEffect(() => {
    if (name) {
      // Clear stale data before fetching to prevent showing previous crew's content
      crewStore.selectedCrew = null
      crewStore.fetchCrew(name)
    }
  }, [name])

  const crew = crewStore.selectedCrew

  const handleExecute = () => {
    navigate(`/crews/${name}/execute`)
  }

  const handleToggleEnabled = async () => {
    if (!crew) return

    try {
      if (crew.enabled) {
        await crewStore.disableCrew(crew.name)
        toast.success('Crew disabled')
      } else {
        await crewStore.enableCrew(crew.name)
        toast.success('Crew enabled')
      }
    } catch (error) {
      toast.error('Failed to update crew status')
    }
  }

  const getProcessTypeIcon = (processType: string) => {
    const icons: Record<string, typeof ArrowsRightLeftIcon> = {
      sequential: ArrowsRightLeftIcon,
      parallel: Squares2X2Icon,
      hierarchical: ChartBarSquareIcon,
    }
    return icons[processType] || ArrowsRightLeftIcon
  }

  const getProcessTypeDescription = (processType: string) => {
    const descriptions: Record<string, string> = {
      sequential: 'Agents execute one after another, passing results to the next agent in the chain.',
      parallel: 'All agents execute simultaneously, combining their results at the end.',
      hierarchical: 'A manager agent coordinates other agents, delegating tasks and synthesizing results.',
    }
    return descriptions[processType] || 'Unknown process type'
  }

  const getStatusBadgeVariant = (success: boolean) => {
    return success ? 'success' : 'error'
  }

  if (crewStore.isLoading || !crew) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    )
  }

  const ProcessIcon = getProcessTypeIcon(crew.process_type)
  const crewHistory = crewStore.getExecutionHistoryForCrew(crew.name)

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={() => navigate('/crews')}>
            <ArrowLeftIcon className="w-5 h-5" />
          </Button>
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-bold text-foreground">{crew.display_name}</h1>
              <Badge variant={crew.enabled ? 'success' : 'gray'}>
                {crew.enabled ? 'Enabled' : 'Disabled'}
              </Badge>
              <Badge variant="primary" size="sm">v{crew.version}</Badge>
            </div>
            <p className="text-muted-foreground mt-1">{crew.description}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="primary"
            onClick={handleExecute}
            disabled={!crew.enabled}
          >
            <PlayIcon className="w-5 h-5 mr-2" />
            Execute
          </Button>
          <Button
            variant={crew.enabled ? 'danger' : 'primary'}
            onClick={handleToggleEnabled}
          >
            {crew.enabled ? (
              <>
                <XCircleIcon className="w-5 h-5 mr-2" />
                Disable
              </>
            ) : (
              <>
                <CheckCircleIcon className="w-5 h-5 mr-2" />
                Enable
              </>
            )}
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Process Type */}
          <Card>
            <h3 className="text-lg font-semibold text-foreground mb-4">Process Type</h3>
            <div className="flex items-start gap-4">
              <div className="p-3 bg-primary-500/20 rounded-lg">
                <ProcessIcon className="w-8 h-8 text-primary-600" />
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-lg font-medium text-foreground capitalize">
                    {crew.process_type}
                  </span>
                </div>
                <p className="text-sm text-muted-foreground">
                  {getProcessTypeDescription(crew.process_type)}
                </p>
              </div>
            </div>
          </Card>

          {/* Team Members */}
          <Card>
            <h3 className="text-lg font-semibold text-foreground mb-4">
              Team Members ({crew.agents.length})
            </h3>
            <div className="space-y-3">
              {crew.agents.map((agent, index) => (
                <div
                  key={index}
                  className="flex items-start gap-3 p-4 bg-muted/50 border border-border rounded-lg"
                >
                  <div className="flex items-center gap-2 text-sm text-muted-foreground min-w-[24px]">
                    <span className="font-mono font-bold text-primary-600">{index + 1}</span>
                  </div>
                  <div className="p-2 bg-card rounded-lg border border-border">
                    <UserIcon className="w-5 h-5 text-muted-foreground" />
                  </div>
                  <div className="flex-1">
                    <h4 className="font-medium text-foreground">{agent.name}</h4>
                    <p className="text-sm text-muted-foreground mt-1">{agent.role}</p>
                  </div>
                </div>
              ))}
            </div>
          </Card>

          {/* Capabilities */}
          {crew.capabilities && (
            <Card>
              <h3 className="text-lg font-semibold text-foreground mb-4">Capabilities</h3>
              <div className="space-y-4">
                {crew.capabilities.operations && crew.capabilities.operations.length > 0 && (
                  <div>
                    <p className="text-sm font-medium text-foreground mb-2">Operations</p>
                    <div className="flex flex-wrap gap-2">
                      {crew.capabilities.operations.map((op, idx) => (
                        <Badge key={idx} variant="primary" size="sm">
                          {op}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}

                {crew.capabilities.integrations && crew.capabilities.integrations.length > 0 && (
                  <div>
                    <p className="text-sm font-medium text-foreground mb-2">Integrations</p>
                    <div className="flex flex-wrap gap-2">
                      {crew.capabilities.integrations.map((int, idx) => (
                        <Badge key={idx} variant="success" size="sm">
                          {int}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}

                <div className="grid grid-cols-2 gap-4 pt-4 border-t border-border">
                  <div>
                    <p className="text-sm text-muted-foreground">Max Context Tokens</p>
                    <p className="text-lg font-semibold text-foreground">
                      {crew.capabilities.max_context_tokens?.toLocaleString() || 'N/A'}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Features</p>
                    <div className="flex gap-2 mt-1">
                      {crew.capabilities.supports_streaming && (
                        <Badge variant="success" size="sm">
                          <BoltIcon className="w-3 h-3 mr-1" />
                          Streaming
                        </Badge>
                      )}
                      {crew.capabilities.supports_hitl && (
                        <Badge variant="warning" size="sm">
                          <UserIcon className="w-3 h-3 mr-1" />
                          HITL
                        </Badge>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </Card>
          )}

          {/* Recent Executions */}
          <Card>
            <h3 className="text-lg font-semibold text-foreground mb-4">
              Recent Executions ({crewHistory.length})
            </h3>
            {crewHistory.length > 0 ? (
              <div className="space-y-3">
                {crewHistory.slice(0, 10).map((execution, idx) => (
                  <div
                    key={idx}
                    className="flex items-center justify-between p-3 bg-muted/50 rounded-lg hover:bg-muted transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      {execution.response.success ? (
                        <CheckCircleIcon className="w-5 h-5 text-green-500" />
                      ) : (
                        <XCircleIcon className="w-5 h-5 text-red-500" />
                      )}
                      <div>
                        <p className="text-sm font-medium text-foreground truncate max-w-md">
                          {execution.request.prompt.substring(0, 50)}
                          {execution.request.prompt.length > 50 ? '...' : ''}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {formatDistanceToNow(new Date(execution.timestamp), { addSuffix: true })}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      {execution.response.metadata?.token_count && (
                        <span className="text-xs text-muted-foreground">
                          {execution.response.metadata.token_count.toLocaleString()} tokens
                        </span>
                      )}
                      <Badge variant={getStatusBadgeVariant(execution.response.success)}>
                        {execution.response.success ? 'Success' : 'Failed'}
                      </Badge>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <BeakerIcon className="w-12 h-12 mx-auto text-muted-foreground mb-2" />
                <p className="text-muted-foreground text-sm">No executions yet</p>
                <Button
                  variant="primary"
                  size="sm"
                  className="mt-3"
                  onClick={handleExecute}
                  disabled={!crew.enabled}
                >
                  Run First Execution
                </Button>
              </div>
            )}
          </Card>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Metadata */}
          <Card>
            <h3 className="text-lg font-semibold text-foreground mb-4">Metadata</h3>
            <div className="space-y-3">
              <div>
                <p className="text-sm text-muted-foreground">Name</p>
                <p className="text-sm font-mono text-foreground">{crew.name}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Domain</p>
                <div className="mt-1">
                  <Badge variant="primary" size="sm">
                    {crew.domain}
                  </Badge>
                </div>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Version</p>
                <p className="text-sm text-foreground">{crew.version}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Status</p>
                <div className="mt-1">
                  <Badge variant={crew.enabled ? 'success' : 'gray'} size="sm">
                    {crew.enabled ? 'Enabled' : 'Disabled'}
                  </Badge>
                </div>
              </div>
            </div>
          </Card>

          {/* Statistics */}
          <Card>
            <h3 className="text-lg font-semibold text-foreground mb-4">Statistics</h3>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">Total Executions</span>
                <span className="text-lg font-semibold text-foreground">
                  {crewHistory.length}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">Team Members</span>
                <span className="text-lg font-semibold text-foreground">
                  {crew.agents.length}
                </span>
              </div>
              {crewHistory.length > 0 && (
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Success Rate</span>
                  <span className="text-lg font-semibold text-green-600">
                    {(
                      (crewHistory.filter((e) => e.response.success).length /
                        crewHistory.length) *
                      100
                    ).toFixed(0)}
                    %
                  </span>
                </div>
              )}
            </div>
          </Card>

          {/* Quick Actions */}
          <Card className="bg-blue-500/10 border-blue-500/20">
            <div className="flex items-start gap-3">
              <CpuChipIcon className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="text-sm font-semibold text-blue-600 mb-1">
                  Multi-Agent Execution
                </h3>
                <p className="text-xs text-blue-600">
                  This crew uses {crew.process_type} coordination to execute {crew.agents.length} agents
                  working together on your tasks.
                </p>
              </div>
            </div>
          </Card>
        </div>
      </div>
    </div>
  )
})

export default CrewDetailPage
